const hasValue = (value) => value !== undefined && value !== null;

const normalizeText = (value) => String(value ?? '').toLowerCase();

export const resolveWidgetFromUserItem = (userItem, widgetData = {}) => {
  if (!hasValue(userItem)) {
    return null;
  }

  if (typeof userItem === 'string' || typeof userItem === 'number') {
    return widgetData?.[userItem] ?? widgetData?.[String(userItem)] ?? null;
  }

  if (typeof userItem === 'object') {
    if (Array.isArray(userItem.category) && userItem.displayName && userItem.sizes) {
      return userItem;
    }

    const resolvedId = hasValue(userItem.id) ? userItem.id : userItem.widgetId;
    if (!hasValue(resolvedId)) {
      return null;
    }

    return widgetData?.[resolvedId] ?? widgetData?.[String(resolvedId)] ?? null;
  }

  return null;
};

export const widgetMatchesSearchQuery = (widget, searchQuery) => {
  const normalizedQuery = normalizeText(searchQuery).trim();
  if (!normalizedQuery) {
    return true;
  }

  const searchableFields = [
    widget?.category?.[0],
    widget?.category?.[1],
    widget?.displayName,
    widget?.name,
  ];

  return searchableFields.some((field) => normalizeText(field).includes(normalizedQuery));
};

export const groupWidgetsByCategory = ({ userList, widgetData, searchQuery }) => {
  if (!Array.isArray(userList) || !widgetData) {
    return [];
  }

  const groupedCategories = new Map();

  userList.forEach((userItem) => {
    const widget = resolveWidgetFromUserItem(userItem, widgetData);
    if (!widget || !hasValue(widget.id) || !widgetMatchesSearchQuery(widget, searchQuery)) {
      return;
    }

    const categoryName = widget?.category?.[0] || 'Other';
    const subCategoryName = widget?.category?.[1] || 'Other';

    if (!groupedCategories.has(categoryName)) {
      groupedCategories.set(categoryName, new Map());
    }

    const subCategoryMap = groupedCategories.get(categoryName);
    if (!subCategoryMap.has(subCategoryName)) {
      subCategoryMap.set(subCategoryName, []);
    }

    const widgetsInSubCategory = subCategoryMap.get(subCategoryName);
    const widgetAlreadyAdded = widgetsInSubCategory.some(
      (existingWidget) => String(existingWidget.id) === String(widget.id),
    );
    if (!widgetAlreadyAdded) {
      widgetsInSubCategory.push({ ...widget });
    }
  });

  return Array.from(groupedCategories.entries()).map(([category, subCategoryMap]) => ({
    category,
    subItems: Array.from(subCategoryMap.entries()).map(([subCategory, widgets]) => ({
      category: subCategory,
      widgets,
    })),
  }));
};

export const buildWidgetLookup = ({ userList, widgetData }) => {
  const widgetLookup = new Map();

  if (!Array.isArray(userList) || !widgetData) {
    return widgetLookup;
  }

  userList.forEach((userItem) => {
    const widget = resolveWidgetFromUserItem(userItem, widgetData);
    if (!widget || !hasValue(widget.id)) {
      return;
    }

    widgetLookup.set(String(widget.id), widget);
  });

  return widgetLookup;
};
